import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { PAYROLL_COMPONENT_BY_TYPE } from '@accounting/shared';
import { usePayrollRun, useVoidPayrollRun } from '../../hooks/usePayroll';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { ApiError } from '../../lib/api';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/payroll/$id')({
  component: PayrollRunDetail,
});

function label(type: string): string {
  return PAYROLL_COMPONENT_BY_TYPE[type]?.label ?? type;
}

function PayrollRunDetail() {
  const { id } = Route.useParams();
  const companyId = useActiveCompanyId();
  const { data: run, isLoading, error } = usePayrollRun(companyId, id);
  const voidRun = useVoidPayrollRun(companyId);
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');

  if (!companyId) return <div className="card" style={{ textAlign: 'center', padding: 32 }}><p className="muted">No active company.</p></div>;
  if (isLoading) return <p className="muted">Loading…</p>;
  if (error || !run) return <p className="field-error">Failed to load payroll run.</p>;

  const voidErr =
    voidRun.error instanceof ApiError
      ? (voidRun.error.body as { error?: string } | null)?.error ?? 'Void failed'
      : voidRun.error?.message;

  return (
    <div>
      <Link to="/payroll" style={{ fontSize: 13 }}>← Payroll</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>
          Payroll — {formatAccountingDate(run.payDate)}{' '}
          {run.status === 'void' ? <span className="tag">void</span> : <span className="tag ok">posted</span>}
        </h1>
        {run.status === 'posted' && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to="/payroll/$id/edit" params={{ id: run.id }} className="btn-secondary" style={{ textDecoration: 'none' }}>
              Edit
            </Link>
            <button className="btn-secondary" onClick={() => setVoiding((v) => !v)}>Void run</button>
          </div>
        )}
      </div>

      {voiding && run.status === 'posted' && (
        <div className="card" style={{ padding: 16, marginBottom: 16, maxWidth: 560 }}>
          <strong style={{ fontSize: 14 }}>Void this payroll run?</strong>
          <p className="muted" style={{ fontSize: 13, margin: '4px 0 8px' }}>
            This voids the run and its journal entry. Blocked if the entry is locked by a completed reconciliation.
          </p>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason"
            maxLength={500}
            style={{ width: '100%', marginBottom: 8 }}
          />
          {voidErr && <div className="field-error" style={{ marginBottom: 8 }}>{voidErr}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn-primary"
              disabled={!reason.trim() || voidRun.isPending}
              onClick={() =>
                voidRun.mutate(
                  { id, reason: reason.trim() },
                  { onSuccess: () => { setVoiding(false); setReason(''); } },
                )
              }
            >
              {voidRun.isPending ? 'Voiding…' : 'Confirm void'}
            </button>
            <button className="btn-secondary" onClick={() => setVoiding(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 16, marginBottom: 16, maxWidth: 560 }}>
        <div className="form-grid" style={{ marginBottom: 0 }}>
          <span className="muted">Pay period</span><span>{formatAccountingDate(run.periodStart)} – {formatAccountingDate(run.periodEnd)}</span>
          <span className="muted">Total gross</span><span>{formatCurrency(run.totalGross)}</span>
          <span className="muted">Total net pay</span><strong>{formatCurrency(run.totalNet)}</strong>
          <span className="muted">Memo</span><span>{run.memo ?? '—'}</span>
          <span className="muted">Journal entry</span>
          <span>
            {run.journalEntryId ? (
              <Link to="/journal/$id" params={{ id: run.journalEntryId }}>View journal entry</Link>
            ) : '—'}
          </span>
          {run.status === 'void' && (
            <>
              <span className="muted">Void reason</span><span>{run.voidReason ?? '—'}</span>
            </>
          )}
        </div>
      </div>

      {run.paystubs.map((stub) => (
        <div key={stub.id} className="card" style={{ padding: 16, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
            <strong>{stub.employeeName}</strong>
            <span className="muted" style={{ fontSize: 13 }}>
              Gross {formatCurrency(stub.grossPay)} · Net {formatCurrency(stub.netPay)}
              {Number(stub.ownerReimbursable) > 0 && (
                <> · Owner owes {formatCurrency(stub.ownerReimbursable)}</>
              )}
            </span>
          </div>
          <table className="table" style={{ marginTop: 8 }}>
            <tbody>
              {stub.components.map((c) => (
                <tr key={c.id}>
                  <td>
                    {label(c.componentType)}
                    <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>
                      ({PAYROLL_COMPONENT_BY_TYPE[c.componentType]?.side === 'debit' ? 'Dr' : 'Cr'})
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(c.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {run.totals.length > 0 && (
        <div className="card" style={{ padding: 16 }}>
          <h2 style={{ fontSize: 15, marginTop: 0 }}>Run totals by component</h2>
          <table className="table">
            <tbody>
              {run.totals.map((t) => (
                <tr key={t.componentType}>
                  <td>{label(t.componentType)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(t.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
